import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, Button, Card, IndexTable, Page, Text } from '@shopify/polaris';

import { api } from '@/api/client';
import { getAdmin } from '@/auth/session';

interface Admin {
  id: number;
  username: string;
  display_name: string | null;
  created_at: number;
  disabled_at: number | null;
  passkey_count: number;
}

export function AdminsPage() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const me = getAdmin();

  const load = useCallback(() => {
    void api<{ admins: Admin[] }>('/ops/api/admins')
      .then((r) => setAdmins(r.admins ?? []))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const toggle = useCallback(
    async (a: Admin) => {
      setError(null);
      try {
        await api(`/ops/api/admins/${a.id}/${a.disabled_at ? 'activate' : 'deactivate'}`, {
          method: 'PUT',
        });
        load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load]
  );

  return (
    <Page title="Admins" subtitle="Disabling takes effect immediately, not when a token expires.">
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Banner>
      )}
      <Card>
        <IndexTable
          resourceName={{ singular: 'admin', plural: 'admins' }}
          itemCount={admins.length}
          selectable={false}
          headings={[
            { title: 'Username' },
            { title: 'Passkeys' },
            { title: 'Status' },
            { title: '' },
          ]}
        >
          {admins.map((a, i) => (
            <IndexTable.Row id={String(a.id)} key={a.id} position={i}>
              <IndexTable.Cell>
                <Text as="span" fontWeight="semibold">
                  {a.username}
                </Text>
                {a.id === me?.id ? <Badge tone="info">You</Badge> : null}
              </IndexTable.Cell>
              <IndexTable.Cell>{a.passkey_count}</IndexTable.Cell>
              <IndexTable.Cell>
                {a.disabled_at ? (
                  <Badge tone="critical">Disabled</Badge>
                ) : (
                  <Badge tone="success">Active</Badge>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {/* ⚠ You cannot disable yourself — with one admin that is an
                    unrecoverable lockout, since there is no password reset. */}
                <Button
                  size="slim"
                  tone={a.disabled_at ? undefined : 'critical'}
                  disabled={a.id === me?.id}
                  onClick={() => void toggle(a)}
                >
                  {a.disabled_at ? 'Activate' : 'Disable'}
                </Button>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>
    </Page>
  );
}
